import { NpcFacePill } from '@/components/admin/npc/npc-face-pill';
import {
  type CurrentExpected,
  type GridCell,
  type GridRow,
  gridRows,
  noteBackground,
} from '@/lib/npc-expected-synthesis';
import { replayStartedLabel } from '@/lib/replay-mark';
import { cn } from '@/lib/utils';
import { NOYAU_CODES, type NpcReplayRow, isNoyauCode, noyauOf } from '@matter_extractor/shared';

/**
 * The grid of the six notes of the tab « Rejeux » (npc.md §26.4, H40, planche 3 `Rejeux.dc.html`;
 * issue #216, lot L): one line per replay, in the order of the table under it, a column per noyau in
 * the order of the table of `noyau.md` §2.1.
 *
 * ## The fill of a case IS the note
 *
 * A case darkens with its note by a `color-mix` of the bleu nuit and the white, written in tokens
 * (`var(--color-bleu-nuit)`, `var(--bg-surface)`): the only inline style of the grid, justified like
 * the width of a rail of `criteres` - the value IS what the operator reads. It stops at 45 % of
 * bleu nuit so that the figure keeps AA contrast on the darkest case.
 *
 * ## Told in words and in shapes, never by a colour alone
 *
 * The retained noyau is ringed in ink and bold, the expected one in dashes of bleu nuit, and each
 * says it to a screen reader (`sr-only`). A replay with no found noyau keeps its line, empty, so
 * that four replays out of five never read as five out of five.
 */

export interface NpcReplaysGridProps {
  readonly rows: readonly NpcReplayRow[];
  /** The current expected noyau, which titles its column « attendu »; null when it is no triplet. */
  readonly current: CurrentExpected | null;
}

const HEAD = 'px-1 py-1.5 text-center align-bottom font-normal text-small';

export function NpcReplaysGrid({ rows, current }: NpcReplaysGridProps) {
  const lines = gridRows(rows);
  const expectedColumn =
    current !== null && current.expected !== null && isNoyauCode(current.expected)
      ? current.expected
      : null;
  return (
    <section
      data-testid="npc-rejeux-grille-section"
      aria-labelledby="npc-rejeux-grille-titre"
      className="space-y-2"
    >
      <h3 id="npc-rejeux-grille-titre" className="font-semibold text-body text-gray-900">
        Les six notes de chaque rejeu
      </h3>
      <div className="overflow-x-auto">
        <table
          data-testid="npc-rejeux-grille"
          className="w-full min-w-[56rem] border-separate border-spacing-1.5"
        >
          <thead>
            <tr>
              <th scope="col" className={cn(HEAD, 'text-left text-gray-700')}>
                <span className="sr-only">Rejeu</span>
              </th>
              {NOYAU_CODES.map((code) => (
                <th
                  key={code}
                  scope="col"
                  data-testid="npc-rejeux-grille-colonne"
                  data-noyau={code}
                  data-expected={expectedColumn === code ? 'true' : undefined}
                  className={cn(
                    HEAD,
                    'rounded-md border-2 border-transparent text-gray-900',
                    expectedColumn === code && 'border-bleu-nuit-deep border-dashed',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="mx-auto mb-0.5 block size-3 rounded-full"
                    style={{ backgroundColor: noyauOf(code).color }}
                  />
                  <span className="block font-bold">{code}</span>
                  <span className="block min-h-5 font-semibold text-bleu-nuit-deep text-caption">
                    {expectedColumn === code ? 'attendu' : ''}
                  </span>
                </th>
              ))}
              <th scope="col" className={cn(HEAD, 'text-left text-gray-700')}>
                Face à l’attendu
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <GridLine key={line.sessionId} line={line} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-caption text-gray-700 leading-snug">
        Le fond se fonce avec la note ; le retenu est cerné d’encre et en gras ; l’attendu est cerné
        de pointillés bleu nuit. Un rejeu sans trouvé garde sa ligne, vide.
      </p>
    </section>
  );
}

function GridLine({ line }: { line: GridRow }) {
  return (
    <tr data-testid="npc-rejeux-grille-ligne" data-session={line.sessionId}>
      <th scope="row" className="min-w-52 px-1 py-1 text-left align-middle font-normal">
        <span className="block font-semibold text-small">{replayStartedLabel(line.startedAt)}</span>
        <span className="block text-caption text-gray-700">
          {line.experience} · {line.series}
        </span>
      </th>
      {line.cells.map((cell) => (
        <Cell key={cell.noyau} cell={cell} />
      ))}
      <td className="px-1 py-1 align-middle">
        <NpcFacePill face={line.face} testId="npc-rejeux-grille-face" showNote />
      </td>
    </tr>
  );
}

function Cell({ cell }: { cell: GridCell }) {
  return (
    <td
      data-testid="npc-rejeux-case"
      data-noyau={cell.noyau}
      data-score={cell.score ?? undefined}
      data-mix={cell.mix ?? undefined}
      data-retained={cell.retained ? 'true' : undefined}
      data-expected={cell.expected ? 'true' : undefined}
      className={cn(
        'h-12 min-w-14 rounded-md border text-center align-middle text-body text-gray-900',
        cell.score === null && 'bg-gray-50',
        cell.retained ? 'border-2 border-gray-900 font-bold' : 'border-gray-100',
        cell.expected && !cell.retained && 'border-2 border-bleu-nuit-deep border-dashed',
        cell.expected &&
          cell.retained &&
          'outline-dashed outline-2 outline-bleu-nuit-deep outline-offset-2',
      )}
      {...(cell.score === null ? {} : { style: { backgroundColor: noteBackground(cell.score) } })}
    >
      {cell.score === null ? null : cell.score}
      {cell.retained ? <span className="sr-only"> (retenu)</span> : null}
      {cell.expected ? <span className="sr-only"> (attendu)</span> : null}
    </td>
  );
}
